export type IframeAllowlistEntry = {
  host: string;
  pathPrefix?: string;
};

export const DEFAULT_IFRAME_ALLOWLIST: IframeAllowlistEntry[] = [
  { host: 'www.youtube.com', pathPrefix: '/embed/' },
  { host: 'www.youtube-nocookie.com', pathPrefix: '/embed/' },
  { host: 'player.vimeo.com', pathPrefix: '/video/' },
  { host: 'www.loom.com', pathPrefix: '/embed/' },
  { host: 'scratch.mit.edu', pathPrefix: '/projects/' },
  { host: 'itch.io', pathPrefix: '/embed/' },
  { host: '*.itch.zone', pathPrefix: '/html/' },
  { host: 'h5p.org', pathPrefix: '/h5p/embed/' },
  { host: 'h5p.com', pathPrefix: '/content/' },
];

export const isValidHostPattern = (pattern: string): boolean => {
  if (!pattern.includes('*')) return pattern.length > 0;
  if (!pattern.startsWith('*.')) return false;
  const suffix = pattern.slice(2);
  return !suffix.includes('*') && suffix.includes('.');
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const normalizeIframeAllowlist = (
  raw: unknown,
): IframeAllowlistEntry[] => {
  if (!Array.isArray(raw)) return [];

  const normalized: IframeAllowlistEntry[] = [];
  raw.forEach(entry => {
    if (!isPlainObject(entry)) return;
    const { host, pathPrefix } = entry;
    if (typeof host !== 'string' || !isValidHostPattern(host)) return;
    if (
      pathPrefix !== undefined &&
      pathPrefix !== null &&
      typeof pathPrefix !== 'string'
    )
      return;
    normalized.push(
      typeof pathPrefix === 'string' ? { host, pathPrefix } : { host },
    );
  });
  return normalized;
};

const hostMatches = (host: string, pattern: string): boolean => {
  if (pattern.startsWith('*.')) {
    return host.endsWith(pattern.slice(1));
  }
  return host === pattern;
};

export const isAllowedIframeSrc = (
  value: string,
  allowlist: IframeAllowlistEntry[] = DEFAULT_IFRAME_ALLOWLIST,
): boolean => {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }

  if (parsed.protocol !== 'https:') return false;
  if (parsed.username || parsed.password) return false;
  if (!parsed.hostname) return false;

  const host = parsed.hostname.toLowerCase();
  return allowlist.some(({ host: allowedHost, pathPrefix }) => {
    if (!hostMatches(host, allowedHost.toLowerCase())) return false;
    if (pathPrefix && !parsed.pathname.startsWith(pathPrefix)) return false;
    return true;
  });
};
